import { makeAutoObservable, runInAction } from 'mobx';
import type { SubscriptionPayment } from '../api/subscription_payment_api';
import { subscriptionPaymentApi } from '../api/subscription_payment_api';

export class SubscriptionPaymentStore {
  // Payment storage
  payments: Map<string, SubscriptionPayment> = new Map();
  loading = false;
  cursor: string | null = null;
  hasMore = true;

  // For manager access - track which user we're viewing
  currentUserUuid: string | null = null;

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed getters
  get allPayments(): SubscriptionPayment[] {
    return Array.from(this.payments.values()).sort((a, b) => {
      const dateA = a.date_created ? new Date(a.date_created).getTime() : 0;
      const dateB = b.date_created ? new Date(b.date_created).getTime() : 0;
      return dateB - dateA;
    });
  }

  // Actions
  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch current user's own subscription payments
   * @param refresh - If true, clears existing data and fetches from beginning
   */
  async fetchPayments(refresh = false): Promise<void> {
    if (this.loading) return;
    if (!refresh && !this.hasMore) return;

    // If we were viewing another user, treat as refresh
    if (this.currentUserUuid !== null) {
      refresh = true;
    }

    runInAction(() => {
      this.loading = true;
      this.error = null;
      if (refresh) {
        this.payments.clear();
        this.cursor = null;
        this.hasMore = true;
        this.currentUserUuid = null;
      }
    });

    try {
      const cursor = refresh ? undefined : this.cursor || undefined;
      const response = await subscriptionPaymentApi.list(cursor);

      runInAction(() => {
        response.items.forEach((payment) => {
          this.payments.set(payment.key, payment);
        });
        this.cursor = response.cursor;
        this.hasMore = response.has_more;
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch payments';
      });
    }
  }

  /**
   * Fetch subscription payments for a specific user (manager access)
   * @param userUuid - The user UUID to fetch payments for
   * @param refresh - If true, clears existing data and fetches from beginning
   */
  async fetchManagerPayments(
    userUuid: string,
    refresh = false
  ): Promise<void> {
    if (this.loading) return;
    if (!refresh && !this.hasMore) return;

    // If switching to a different user, treat as refresh
    if (userUuid !== this.currentUserUuid) {
      refresh = true;
    }

    runInAction(() => {
      this.loading = true;
      this.error = null;
      if (refresh) {
        this.payments.clear();
        this.cursor = null;
        this.hasMore = true;
        this.currentUserUuid = userUuid;
      }
    });

    try {
      const cursor = refresh ? undefined : this.cursor || undefined;
      const response = await subscriptionPaymentApi.managerList(
        userUuid,
        cursor
      );

      runInAction(() => {
        response.items.forEach((payment) => {
          this.payments.set(payment.key, payment);
        });
        this.cursor = response.cursor;
        this.hasMore = response.has_more;
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch payments';
      });
    }
  }

  /**
   * Clear all payment data (useful when switching contexts)
   */
  clear(): void {
    this.payments.clear();
    this.cursor = null;
    this.hasMore = true;
    this.currentUserUuid = null;
    this.error = null;
  }
}

export const subscriptionPaymentStore = new SubscriptionPaymentStore();
